import { api } from "@/lib/api/client";
import type { paths } from "@/lib/api/generated/api";

/**
 * ARKA PLAN BİLDİRİMİ — tarayıcı tarafı (KARARLAR K6).
 *
 * WHAT THIS IS FOR. The live stream lives as long as a tab lives. A phone with the browser in the
 * background — the case this whole feature exists for — has no tab, and neither does a closed
 * laptop. Web Push is the only channel the operating system keeps for us.
 *
 * THE PERMISSION IS ASKED FROM A REAL CLICK, NEVER ON LOAD. A page that asks on arrival is the
 * fastest way to a permanently blocked origin: the browser remembers "denied" and there is no
 * second chance — not for this feature, and not for any later one. So `enable()` is only ever
 * reached from the switch in profile settings, and `Notification.requestPermission()` runs inside
 * that click's own task, which is also what Safari requires (a user gesture).
 *
 * NOTHING HERE READS A MESSAGE. The subscription is an endpoint plus two keys; the payload the
 * server later sends carries a kind and an address, and `public/sw.js` writes the Turkish line.
 */

type ConfigResponse =
  paths["/api/v1/notifications/push/config"]["get"]["responses"][200]["content"]["application/json"];
export type PushConfig = ConfigResponse["data"];

type SubscriptionsResponse =
  paths["/api/v1/notifications/push/subscriptions"]["get"]["responses"][200]["content"]["application/json"];
export type PushSubscriptionRow = SubscriptionsResponse["data"][number];

export const pushApi = {
  config(): Promise<PushConfig> {
    return api.get<PushConfig>("/notifications/push/config");
  },

  list(): Promise<PushSubscriptionRow[]> {
    return api.get<PushSubscriptionRow[]>("/notifications/push/subscriptions");
  },

  subscribe(subscription: PushSubscriptionJSON): Promise<PushSubscriptionRow> {
    return api.put<PushSubscriptionRow>("/notifications/push/subscriptions", {
      provider: "webpush",
      endpoint: subscription.endpoint,
      keys: subscription.keys,
    });
  },

  unsubscribe(endpoint: string): Promise<void> {
    return api.post<void>("/notifications/push/subscriptions/remove", { endpoint });
  },

  remove(id: string): Promise<void> {
    return api.delete(`/notifications/push/subscriptions/${id}`);
  },
};

export interface PushSubscriptionJSON {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/** Why background notifications are not available, when they are not. */
export type PushUnavailable =
  /** The browser has no service worker or no Push API (an old Safari, a private window). */
  | "unsupported"
  /** The installation has no VAPID keys: nothing to subscribe to. */
  | "not-configured"
  /** The person said no, and only they can undo it — through the browser's own site settings. */
  | "denied";

export type PushState =
  | { status: "unavailable"; reason: PushUnavailable }
  | { status: "off" }
  | { status: "on"; endpoint: string };

export function pushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * The VAPID public key travels as URL-safe base64 and the Push API wants bytes.
 *
 * Written out rather than pulled from a library: it is eight lines, and a dependency whose only
 * job is one encoding is a supply-chain risk for nothing.
 */
export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  // The buffer is created explicitly so the type is `Uint8Array<ArrayBuffer>`: `applicationServerKey`
  // will not take a view that might sit on a `SharedArrayBuffer`.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  // `type: "classic"` and the root scope on purpose: the worker must be able to open any in-app
  // address a notification names.
  return navigator.serviceWorker.register("/sw.js", { scope: "/" });
}

/** What the settings screen should show, without asking the person anything. */
export async function readPushState(config: PushConfig | null): Promise<PushState> {
  if (!pushSupported()) return { status: "unavailable", reason: "unsupported" };
  if (!config?.enabled || !config.publicKey) return { status: "unavailable", reason: "not-configured" };
  if (Notification.permission === "denied") return { status: "unavailable", reason: "denied" };
  try {
    const existing = await (await registration()).pushManager.getSubscription();
    return existing ? { status: "on", endpoint: existing.endpoint } : { status: "off" };
  } catch {
    return { status: "unavailable", reason: "unsupported" };
  }
}

/**
 * Turns background notifications ON for this browser. MUST be called from a click.
 *
 * Order matters: permission first (so a refusal costs no request), then the subscription, then the
 * server. A subscription the server never heard about is a browser that believes it is subscribed
 * and never rings — so the server call is what decides success, and a failure unsubscribes again
 * rather than leaving the two sides disagreeing.
 */
export async function enablePush(config: PushConfig): Promise<PushState> {
  if (!pushSupported()) return { status: "unavailable", reason: "unsupported" };
  if (!config.enabled || !config.publicKey) return { status: "unavailable", reason: "not-configured" };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { status: "unavailable", reason: permission === "denied" ? "denied" : "unsupported" };
  }

  const worker = await registration();
  const subscription =
    (await worker.pushManager.getSubscription()) ??
    (await worker.pushManager.subscribe({
      // The spec allows a payload-less subscription; ours always carries one, and Chrome refuses
      // to deliver a silent push more than a handful of times.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.publicKey),
    }));

  const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    await subscription.unsubscribe();
    return { status: "unavailable", reason: "unsupported" };
  }

  try {
    await pushApi.subscribe({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } });
  } catch (error) {
    await subscription.unsubscribe();
    throw error;
  }
  return { status: "on", endpoint: json.endpoint };
}

/**
 * Turns them off for this browser: the server row goes and the browser's own subscription with it.
 *
 * The SERVER IS TOLD FIRST. The other order leaves a row the server will push to and a browser
 * that has forgotten how to receive — a notification sent into nothing, counted as delivered.
 */
export async function disablePush(): Promise<PushState> {
  if (!pushSupported()) return { status: "unavailable", reason: "unsupported" };
  const worker = await registration();
  const subscription = await worker.pushManager.getSubscription();
  if (subscription) {
    await pushApi.unsubscribe(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  }
  return { status: "off" };
}

/**
 * MAKES THE SERVER AGREE WITH THE BROWSER, IN THE CUSTOMER THE PERSON IS IN NOW.
 *
 * A subscription belongs to the BROWSER; a `push_subscriptions` row belongs to (customer, person).
 * So somebody who turned the switch on in customer A and then moved to customer B had a browser
 * that was subscribed, a screen that said «Bu cihazda açık», and no row in B at all — the worker's
 * `findForUsers(db, tenantId, …)` found nothing and they never got a single background
 * notification there, with no reason on screen to press anything (review, finding 6).
 *
 * The fix is to write the row rather than to correct the sentence: `PUT /subscriptions` is
 * idempotent on (customer, endpoint hash), so re-sending what the browser already holds either
 * creates the missing row or refreshes the one that exists. NOTHING HERE ASKS FOR PERMISSION — it
 * returns untouched unless the browser is already subscribed, which is the whole point of the
 * permission rule (K6): a person who never said yes is never asked by anything but the switch.
 *
 * Never throws: it runs from a mount effect, and a failed reconcile costs a background
 * notification, not a screen.
 */
export async function ensurePushSubscription(): Promise<PushState> {
  // Everything below the first two lines costs a request, so both cheap facts are checked first:
  // this is mounted on every screen of the shell, and for anybody who never turned the switch on
  // it must cost nothing at all. It deliberately does NOT read the config endpoint — an
  // installation with no keys answers the PUT with 422 and the catch below swallows it.
  if (!pushSupported()) return { status: "unavailable", reason: "unsupported" };
  if (Notification.permission !== "granted") return { status: "off" };
  try {
    const subscription = await (await registration()).pushManager.getSubscription();
    if (!subscription) return { status: "off" };
    const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return { status: "off" };
    await pushApi.subscribe({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } });
    return { status: "on", endpoint: json.endpoint };
  } catch {
    // The browser may still be subscribed; the row is what could not be written. Reported as
    // «off» would be a lie in the other direction, so the caller simply learns nothing changed.
    return { status: "unavailable", reason: "unsupported" };
  }
}

/**
 * Sign-out: this browser stops ringing for the person who just left.
 *
 * Best-effort and never throws — it runs inside the sign-out path, and a push service that is
 * slow must not keep somebody signed in. The server has a second line of defence (a subscription
 * whose device has no live session is skipped, `push.md` §2); this one is what makes the device
 * quiet immediately.
 */
export async function forgetPushOnSignOut(): Promise<void> {
  try {
    if (!pushSupported()) return;
    const worker = await navigator.serviceWorker.getRegistration("/");
    const subscription = await worker?.pushManager.getSubscription();
    if (!subscription) return;
    await pushApi.unsubscribe(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  } catch {
    // Nothing here is worth failing a sign-out for.
  }
}
